// 阅读系统服务管理器 - 统一初始化和管理所有服务

import { IndexedDBManager } from './indexedDBManager';
import { LocalStorageManagerImpl } from './localStorageManager';
import { BookSourceManager } from './bookSourceManager';
import { BookSourceOperations } from './bookSourceOperations';
import { BookParser } from './bookParser';
import { BookSearchManager } from './bookSearchManager';
import { BookLibrary } from './bookLibrary';
import { SearchService } from './searchService';
import { CategoryManager } from './categoryManager';
import { CategoryBrowser } from './categoryBrowser';
import { ReadingEngine } from './readingEngine';
import { ReadingAssistant } from './readingAssistant';
import { UserBehaviorTracker } from './userBehaviorTracker';
import { RecommendationEngine } from './recommendationEngine';
import { CacheManager, initializeCacheManager } from './cacheManager';

export class ReadingServiceManager {
  private static instance: ReadingServiceManager;
  
  // 存储管理器
  public readonly indexedDBManager: IndexedDBManager;
  public readonly localStorageManager: LocalStorageManagerImpl;
  public readonly cacheManager: CacheManager;
  
  // 核心服务
  public readonly bookSourceManager: BookSourceManager;
  public readonly bookSourceOperations: BookSourceOperations;
  public readonly bookParser: BookParser;
  public readonly bookSearchManager: BookSearchManager;
  public readonly bookLibrary: BookLibrary;
  public readonly searchService: SearchService;
  public readonly categoryManager: CategoryManager;
  public readonly categoryBrowser: CategoryBrowser;
  public readonly readingEngine: ReadingEngine;
  public readonly readingAssistant: ReadingAssistant;
  public readonly userBehaviorTracker: UserBehaviorTracker;
  public readonly recommendationEngine: RecommendationEngine;
  
  private constructor() {
    // 初始化存储管理器
    this.indexedDBManager = new IndexedDBManager();
    this.localStorageManager = new LocalStorageManagerImpl();
    
    // 初始化缓存管理器
    this.cacheManager = initializeCacheManager(this.indexedDBManager, {
      maxCacheSize: 100, // 100MB
      defaultTTL: 24, // 24小时
      cleanupThreshold: 0.8, // 80%
      preloadEnabled: true,
      compressionEnabled: true
    });
    
    // 初始化核心服务
    this.bookSourceManager = new BookSourceManager(this.indexedDBManager);
    this.bookSourceOperations = new BookSourceOperations(this.bookSourceManager);
    this.bookParser = new BookParser();
    this.bookSearchManager = new BookSearchManager(this.bookSourceManager, this.indexedDBManager);
    this.bookLibrary = new BookLibrary(this.indexedDBManager, this.bookSearchManager);
    this.searchService = new SearchService(this.indexedDBManager, this.bookSearchManager, this.bookLibrary);
    this.categoryManager = new CategoryManager(this.indexedDBManager, this.bookSearchManager, this.bookLibrary);
    this.categoryBrowser = new CategoryBrowser(this.categoryManager, this.searchService, this.indexedDBManager);
    this.readingEngine = new ReadingEngine(this.bookSearchManager, this.indexedDBManager, this.localStorageManager);
    this.readingAssistant = new ReadingAssistant(this.indexedDBManager, this.localStorageManager, this.readingEngine);
    this.userBehaviorTracker = new UserBehaviorTracker(this.indexedDBManager);
    this.recommendationEngine = new RecommendationEngine(this.userBehaviorTracker, this.bookLibrary, this.bookSearchManager, this.indexedDBManager);
  }
  
  public static getInstance(): ReadingServiceManager {
    if (!ReadingServiceManager.instance) {
      ReadingServiceManager.instance = new ReadingServiceManager();
    }
    return ReadingServiceManager.instance;
  }
  
  // 初始化所有服务
  public async initialize(): Promise<void> {
    try {
      // 初始化数据库
      await this.indexedDBManager.initialize();
      
      // 初始化缓存管理器（自动清理过期缓存）
      await this.cacheManager.clear(); // 清理过期缓存
      
      // 初始化其他需要异步初始化的服务
      console.log('Reading services initialized successfully');
    } catch (error) {
      console.error('Failed to initialize reading services:', error);
      throw error;
    }
  }
  
  // 清理资源
  public async cleanup(): Promise<void> {
    try {
      // 清理缓存管理器
      this.cacheManager.destroy();
      
      // 清理其他服务的资源
      console.log('Reading services cleaned up');
    } catch (error) {
      console.error('Failed to cleanup reading services:', error);
    }
  }
}

// 全局服务管理器实例
export const globalServiceManager = ReadingServiceManager.getInstance();